/**
 * AI usage and credit, as the business sees it.
 *
 * Charges only. What CenoX pays its AI provider, the markup and the margin
 * are CenoX's business and never appear here — the API does not send them.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Card, CardHeader, Spinner } from '../../components/ui';

interface AiBilling {
  billingMode: 'cenox_managed' | 'bring_your_own_key' | 'unavailable';
  managedInPlan: boolean;
  planName: string;
  currencyCode: string;
  includedCredit: string;
  includedUsed: string;
  includedRemaining: string;
  topUpBalance: string;
  chargedThisCycle: string;
  cycleStart: string;
  cycleEnd: string;
  history: Array<{ at: string; feature: string; tokens: number; charged: string; status: string }>;
}

const day = (iso: string) => new Date(iso).toLocaleDateString();

export function AiUsageCard(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const q = useQuery({
    queryKey: ['ai-billing', business?.businessId],
    queryFn: () => api.get<AiBilling>(path('/ai/billing')),
    enabled: Boolean(business),
  });

  if (q.isPending) return <Card><Spinner /></Card>;
  if (q.isError || !q.data) return null;
  const b = q.data;
  const amount = (v: string) => `${b.currencyCode} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card>
      <CardHeader
        title="AI usage"
        subtitle={`${b.planName} plan · billing cycle ${day(b.cycleStart)} – ${day(b.cycleEnd)}`}
      />
      {b.billingMode === 'bring_your_own_key' && (
        <p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          You use your own OpenAI API key. Usage is billed to you by OpenAI directly; CenoX does
          not charge AI credit for it.
        </p>
      )}
      {b.billingMode === 'unavailable' && (
        <p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {b.managedInPlan
            ? 'CenoX-managed AI for your plan is not switched on yet. Until it is, add your own OpenAI API key in Settings to use the assistant.'
            : 'CenoX-managed AI is not included in this plan. Add your own OpenAI API key in Settings to use the assistant.'}
        </p>
      )}
      {b.billingMode === 'cenox_managed' && (
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          {[
            ['Included this cycle', b.includedCredit],
            ['Used', b.includedUsed],
            ['Remaining', b.includedRemaining],
            ['Top-up balance', b.topUpBalance],
            ['AI charges this cycle', b.chargedThisCycle],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-slate-200 px-3 py-2">
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="mt-0.5 font-medium text-slate-900">{amount(value!)}</dd>
            </div>
          ))}
        </dl>
      )}
      {b.history.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="py-1.5 pr-3 font-medium">When</th>
                <th className="py-1.5 pr-3 font-medium">Used for</th>
                <th className="py-1.5 pr-3 font-medium">Tokens</th>
                <th className="py-1.5 font-medium">Charged</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {b.history.map((h, i) => (
                <tr key={i}>
                  <td className="py-1.5 pr-3 text-slate-600">{new Date(h.at).toLocaleString()}</td>
                  <td className="py-1.5 pr-3 capitalize text-slate-600">{h.feature}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{h.tokens.toLocaleString()}</td>
                  <td className="py-1.5 text-slate-900">
                    {h.status === 'not_charged' ? 'Not charged' : h.status === 'byok' ? 'Your key' : amount(h.charged)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
